import { forwardRef, useRef } from 'react';
import { Box } from '@mui/material';
import { splitTextByChannelMention } from '../utils/channelMention';

type Props = {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyUp?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
};

export const MentionTextField = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      value,
      onChange,
      onKeyUp,
      placeholder,
      className = '',
      inputClassName = '',
    },
    ref,
  ) => {
    const backdropRef = useRef<HTMLDivElement>(null);
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef =
      (ref as React.RefObject<HTMLTextAreaElement>) || internalRef;

    // Sync scroll between textarea and backdrop
    const handleScroll = () => {
      if (backdropRef.current && textareaRef.current) {
        backdropRef.current.scrollTop = textareaRef.current.scrollTop;
        backdropRef.current.scrollLeft = textareaRef.current.scrollLeft;
      }
    };

    const parts = splitTextByChannelMention(value);

    return (
      <Box
        className={`relative border border-gray-300 rounded-[2rem] hover:border-gray-400 focus-within:border-purple-1 focus-within:border-2 ${className}`}
      >
        {/* Backdrop layer with highlighted text - renders ALL visible text */}
        <div
          ref={backdropRef}
          className={`absolute inset-0 pointer-events-none overflow-hidden whitespace-pre-wrap break-words px-4 py-4 ${inputClassName}`}
          style={{
            fontFamily: 'inherit',
            fontSize: '16px',
            lineHeight: '1.5',
          }}
          aria-hidden="true"
        >
          {parts.map((part, index) =>
            part.isChannelMention ? (
              <mark
                key={index}
                className="bg-[#EDE5FC] text-[#5F1ECC] rounded-sm"
                style={{ padding: 0, margin: 0 }}
              >
                {part.text}
              </mark>
            ) : (
              <span key={index}>{part.text}</span>
            ),
          )}
        </div>

        {/* Actual textarea - text is transparent, only caret visible */}
        <textarea
          ref={textareaRef}
          className={`relative w-full bg-transparent text-transparent caret-black resize-none outline-none px-4 py-4 placeholder:text-gray-400 ${inputClassName}`}
          style={{
            caretColor: 'black',
            fontSize: '16px',
            lineHeight: '1.5',
            minHeight: '56px',
            WebkitTextFillColor: value ? 'transparent' : undefined,
          }}
          value={value}
          onChange={onChange}
          onKeyUp={onKeyUp}
          onScroll={handleScroll}
          placeholder={placeholder}
          rows={1}
        />
      </Box>
    );
  },
);

MentionTextField.displayName = 'MentionTextField';
